import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiBase } from '@/lib/apiBase';
import { getSessionToken, setFlowOverride, setSessionId, setSessionToken } from '@/lib/flowStorage';
import { trackEvent } from '@/lib/trackEvent';
import PageSeo from '@/seo/PageSeo';
import BrandFormChrome from '@/components/BrandFormChrome';
import { BRAND_NAME } from '@/lib/brandConstants';
import { amountPaiseAfterVoucherPercent } from '@/lib/pricingVoucher';
import {
  brandFormAccentBar,
  brandFormAmbient,
  brandFormCard,
  brandFormHeaderBand,
  brandFormInput,
  brandFormMesh,
  brandFormPage,
  brandFormPrimaryCta,
  brandFormSectionKicker,
} from '@/lib/brandFormShell';

const API_BASE = getApiBase();

function loadRazorpayScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load Razorpay checkout.'));
    document.body.appendChild(s);
  });
}

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState('');
  const [sessionEmail, setSessionEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [sessionSource, setSessionSource] = useState('direct');
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('starter');
  const [voucher, setVoucher] = useState('');
  const [voucherMsg, setVoucherMsg] = useState('');
  /** Partial discount: server-validated code applied to Razorpay checkout. */
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [busy, setBusy] = useState(true);
  const [payBusy, setPayBusy] = useState(false);
  const [err, setErr] = useState('');
  const [recoverPid, setRecoverPid] = useState('');
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverMsg, setRecoverMsg] = useState('');
  const [rzpKey, setRzpKey] = useState('');
  const tokenRef = useRef('');
  const formStartedLogged = useRef(false);

  useEffect(() => {
    const tUrl = searchParams.get('token');
    if (tUrl) setSessionToken(tUrl);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr('');
      setBusy(true);
      try {
        const tUrl = searchParams.get('token');
        const tok = tUrl || getSessionToken();
        tokenRef.current = tok;
        if (tok) setSessionToken(tok);

        let sid = '';
        try {
          sid = sessionStorage.getItem('crc_session_id') || '';
        } catch {
          /* ignore */
        }

        /** Query `token` wins over a stale `crc_session_id` to avoid assessment ↔ payment redirect loops. */
        if (tUrl) {
          const r = await fetch(`${API_BASE}/api/public/session-from-token?token=${encodeURIComponent(tUrl)}`);
          const d = await r.json().catch(() => ({}));
          if (!r.ok) {
            try {
              sessionStorage.removeItem('crc_session_id');
            } catch {
              /* ignore */
            }
            throw new Error(d.error || 'Could not restore session from link.');
          }
          sid = String(d.sessionId || '').trim();
          if (!sid) {
            try {
              sessionStorage.removeItem('crc_session_id');
            } catch {
              /* ignore */
            }
            throw new Error('Could not restore session from link.');
          }
          try {
            sessionStorage.setItem('crc_session_id', sid);
          } catch {
            /* ignore */
          }
          setSessionEmail(String(d.email || '').trim());
          setLeadName(String(d.leadName || 'Student').trim());
          setSessionSource(String(d.source || 'direct'));
        } else if (!sid && tok) {
          const r = await fetch(`${API_BASE}/api/public/session-from-token?token=${encodeURIComponent(tok)}`);
          const d = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(d.error || 'Could not restore session from link.');
          sid = d.sessionId;
          try {
            sessionStorage.setItem('crc_session_id', sid);
          } catch {
            /* ignore */
          }
          setSessionEmail(String(d.email || '').trim());
          setLeadName(String(d.leadName || 'Student').trim());
          setSessionSource(String(d.source || 'direct'));
        }

        if (!sid) {
          const hasToken = Boolean(String(tUrl || tok || '').trim());
          setErr(
            hasToken
              ? 'We could not restore your checkout session from this link. Your payment may still have gone through — open the same site address you used to start (www vs non-www must match), or begin again from /start. If money was debited, contact support with your Razorpay receipt.'
              : 'Your session was not found. Open this page from the same browser where you entered your details, or start again from the beginning. Tip: always use the same site URL (for example https://yoursite.com vs https://www.yoursite.com) for the whole flow.',
          );
          setBusy(false);
          return;
        }
        setSessionId(sid);

        const [cfgRes, sessRes] = await Promise.all([
          fetch(`${API_BASE}/api/public-config`),
          fetch(`${API_BASE}/api/sessions/${sid}`),
        ]);
        const cfg = await cfgRes.json().catch(() => ({}));
        const sd = await sessRes.json().catch(() => ({}));
        if (cancelled) return;
        if (!sessRes.ok) throw new Error(sd.error || 'Session not found.');
        setSessionEmail(String(sd.email || '').trim());
        setLeadName(String(sd.leadName || 'Student').trim());
        setSessionSource(String(sd.source || 'direct'));
        const pl = Array.isArray(cfg.plans) ? cfg.plans : [];
        setPlans(pl);
        setRzpKey(String(cfg.razorpayKeyId || ''));
        const starter = pl.find((p) => p.id === 'starter');
        setSelectedPlanId(starter?.id || pl[0]?.id || 'starter');
        if (String(sd.paymentStatus) === 'paid') {
          const tok = tokenRef.current || getSessionToken();
          if (String(sd.source) === 'school' && tok) {
            navigate(`/report-preview/${encodeURIComponent(tok)}`, { replace: true });
          } else {
            setFlowOverride('direct');
            navigate(
              tok
                ? `/assessment?flow=direct&token=${encodeURIComponent(tok)}`
                : '/assessment?flow=direct',
              { replace: true },
            );
          }
          return;
        }
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  /**
   * Razorpay may mark the session paid via server webhook before `/api/payment/verify` returns.
   * Poll briefly so the user is redirected without pasting Payment ID or refreshing.
   */
  useEffect(() => {
    if (!sessionId || busy || err) return undefined;
    let cancelled = false;
    let ticks = 0;
    const maxTicks = 50;
    const id = setInterval(async () => {
      if (cancelled) return;
      ticks += 1;
      if (ticks > maxTicks) {
        clearInterval(id);
        return;
      }
      try {
        const sessRes = await fetch(`${API_BASE}/api/sessions/${sessionId}`);
        const sd = await sessRes.json().catch(() => ({}));
        if (cancelled || !sessRes.ok) return;
        if (String(sd.paymentStatus) !== 'paid') return;
        clearInterval(id);
        const tok = tokenRef.current || getSessionToken();
        if (String(sd.source) === 'school' && tok) {
          navigate(`/report-preview/${encodeURIComponent(tok)}`, { replace: true });
        } else {
          setFlowOverride('direct');
          navigate(
            tok ? `/assessment?flow=direct&token=${encodeURIComponent(tok)}` : '/assessment?flow=direct',
            { replace: true },
          );
        }
      } catch {
        /* ignore */
      }
    }, 2500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sessionId, busy, err, navigate]);

  const submitPaymentRecovery = useCallback(async () => {
    const pid = recoverPid.replace(/\s+/g, '').trim();
    if (!pid) {
      setRecoverMsg('Paste your Razorpay Payment ID (starts with pay_).');
      return;
    }
    setRecoverBusy(true);
    setRecoverMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/payment/recover-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razorpayPaymentId: pid,
          ...(recoverEmail.trim() ? { reportEmail: recoverEmail.trim() } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Recovery failed (${res.status})`);
      const tok = String(data.sessionToken || '').trim();
      const sid = String(data.sessionId || '').trim();
      if (!tok || !sid) throw new Error('Invalid recovery response.');
      setSessionToken(tok);
      setSessionId(sid);
      try {
        sessionStorage.setItem('crc_session_id', sid);
      } catch {
        /* ignore */
      }
      tokenRef.current = tok;
      setErr('');
      setRecoverMsg(data.alreadyPaid ? 'Session found — taking you to your test.' : 'Payment linked — taking you to your test.');
      navigate(`/payment?token=${encodeURIComponent(tok)}`, { replace: true });
    } catch (e) {
      setRecoverMsg(String(e.message || e));
    } finally {
      setRecoverBusy(false);
    }
  }, [recoverPid, recoverEmail, navigate]);

  useEffect(() => {
    if (!sessionId || busy || err) return;
    if (formStartedLogged.current) return;
    formStartedLogged.current = true;
    void trackEvent('form_started', { sessionId, meta: { surface: 'payment' } });
  }, [sessionId, busy, err]);

  useEffect(() => {
    setAppliedVoucher(null);
    setVoucherMsg('');
  }, [selectedPlanId]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) || plans[0] || null,
    [plans, selectedPlanId],
  );

  const listPricePaise = Number(selectedPlan?.pricePaise || 0);
  const checkoutPaise = useMemo(() => {
    if (!selectedPlan) return 0;
    const base = listPricePaise;
    if (!appliedVoucher || appliedVoucher.percentOff >= 100) return base;
    return amountPaiseAfterVoucherPercent(base, appliedVoucher.percentOff);
  }, [selectedPlan, appliedVoucher, listPricePaise]);

  const choosePlanOnServer = useCallback(async () => {
    if (!sessionId || !selectedPlan) return;
    const res = await fetch(`${API_BASE}/api/sessions/${sessionId}/plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId: selectedPlan.id }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not save plan.');
  }, [sessionId, selectedPlan]);

  const verifyPayment = useCallback(
    async (body) => {
      const tok = getSessionToken();
      const res = await fetch(`${API_BASE}/api/payment/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tok ? { 'X-Session-Token': tok } : {}),
        },
        body: JSON.stringify({ sessionId, ...body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        void trackEvent('payment_failed', {
          sessionId,
          meta: { reason: 'verify_failed', status: res.status, detail: data.error || null },
        });
        throw new Error(data.error || 'Verification failed.');
      }
      /* `payment_success` is recorded server-side in POST /api/payment/verify */
      const t = tokenRef.current || getSessionToken();
      if (sessionSource === 'school' && t) {
        navigate(`/report-preview/${encodeURIComponent(t)}`);
      } else {
        setFlowOverride('direct');
        navigate(
          t ? `/assessment?flow=direct&token=${encodeURIComponent(t)}` : '/assessment?flow=direct',
        );
      }
    },
    [navigate, sessionId, sessionSource],
  );

  const applyVoucher = useCallback(async () => {
    setVoucherMsg('');
    setPayBusy(true);
    try {
      const code = voucher.trim();
      if (!code) {
        setVoucherMsg('Enter a school or partner code.');
        return;
      }
      const vRes = await fetch(`${API_BASE}/api/vouchers/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const v = await vRes.json().catch(() => ({}));
      if (!vRes.ok || !v.ok) {
        setVoucherMsg(v.message || v.error || 'Invalid code.');
        return;
      }
      const pct = Number(v.percentOff) || 0;
      await choosePlanOnServer();
      if (pct >= 100) {
        setAppliedVoucher(null);
        await verifyPayment({ voucherCode: code });
        return;
      }
      const norm = String(v.code || code).trim();
      setAppliedVoucher({
        code: norm,
        percentOff: pct,
        label: String(v.label || '').trim(),
      });
      setVoucherMsg(
        `${pct}% off applied to your selected plan..${v.label ? ` · ${v.label}` : ''}`,
      );
    } catch (e) {
      setVoucherMsg(String(e.message || e));
    } finally {
      setPayBusy(false);
    }
  }, [voucher, verifyPayment, choosePlanOnServer]);

  const payRazorpay = useCallback(async () => {
    setPayBusy(true);
    setVoucherMsg('');
    try {
      if (appliedVoucher && appliedVoucher.percentOff < 100 && checkoutPaise < 100) {
        setVoucherMsg('Discounted amount is below ₹1. Pick another plan or remove the discount.');
        setPayBusy(false);
        return;
      }
      await choosePlanOnServer();
      await loadRazorpayScript();
      const tok = getSessionToken();
      const body = {
        name: leadName,
        reportEmail: sessionEmail,
        email: sessionEmail,
        sessionId,
      };
      if (appliedVoucher && appliedVoucher.percentOff > 0 && appliedVoucher.percentOff < 100) {
        body.voucherCode = appliedVoucher.code;
      }
      const res = await fetch(`${API_BASE}/api/payments/razorpay-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not start checkout.');
      const key = data.keyId || rzpKey;
      if (!key || !window.Razorpay) throw new Error('Payment gateway is not available.');
      const options = {
        key,
        amount: data.amount,
        currency: data.currency || 'INR',
        order_id: data.orderId,
        name: BRAND_NAME,
        description: `${BRAND_NAME} · Career Intelligence Report`,
        prefill: {
          name: leadName,
          email: sessionEmail,
        },
        theme: { color: '#0284c7' },
        handler: async (response) => {
          const payload = {
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          };
          let lastErr;
          try {
            for (let attempt = 0; attempt < 5; attempt++) {
              try {
                await verifyPayment(payload);
                return;
              } catch (e) {
                lastErr = e;
                if (attempt < 4) await new Promise((r) => setTimeout(r, 1000));
              }
            }
            setVoucherMsg(
              `${String(lastErr?.message || lastErr)} If payment succeeded, stay on this page — you will be moved to your test automatically in a few seconds.`,
            );
          } finally {
            setPayBusy(false);
          }
        },
        modal: {
          ondismiss: () => setPayBusy(false),
        },
      };
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (e) {
      setVoucherMsg(String(e.message || e));
      setPayBusy(false);
    }
  }, [choosePlanOnServer, verifyPayment, leadName, sessionEmail, sessionId, rzpKey, appliedVoucher, checkoutPaise]);

  const starter = plans.find((p) => p.id === 'starter');

  return (
    <div className={brandFormPage}>
      <PageSeo title="Payment" description="Choose your plan and pay securely." path="/payment" />
      <div className={brandFormMesh} aria-hidden />
      <div className={brandFormAmbient} aria-hidden />
      <BrandFormChrome />
      <div className="relative z-10 mx-auto max-w-lg space-y-6 pt-0 sm:pt-0">
        <div className={brandFormCard}>
          <div className={brandFormAccentBar} aria-hidden />
          <div className={`${brandFormHeaderBand} text-center sm:text-left`}>
            <p className={brandFormSectionKicker}>Secure checkout</p>
            <h1 className="mt-2 font-display text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">Choose your plan</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Starter (₹1299) is selected by default. Switch plans anytime before you pay.
            </p>
          </div>

          <div className="bg-gradient-to-b from-white to-teal-50/10 px-5 py-5 sm:px-6 sm:pb-7">
            {busy ? (
              <div className="flex flex-col items-center justify-center gap-3 py-14 text-slate-600">
                <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
                <p className="text-sm font-medium">Loading your session…</p>
              </div>
            ) : err ? (
              <div className="space-y-4">
                <p className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-950">
                  {err}
                </p>
                <div className="rounded-xl border border-slate-200 bg-slate-50/90 px-3 py-3 text-left">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Already paid?</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600">
                    If Razorpay charged you but this page lost your session, paste the <strong>Payment ID</strong>{' '}
                    (e.g. <code className="rounded bg-white px-0.5">pay_…</code>) from your SMS, email receipt, or
                    Razorpay dashboard. Optionally add the same email you used on the start form so we can match your
                    checkout.
                  </p>
                  <label className="mt-2 block text-[11px] font-medium text-slate-600" htmlFor="crc-recover-pid">
                    Razorpay Payment ID
                  </label>
                  <input
                    id="crc-recover-pid"
                    className={brandFormInput}
                    value={recoverPid}
                    onChange={(e) => setRecoverPid(e.target.value)}
                    placeholder="pay_XXXXXXXXXXXX"
                    autoComplete="off"
                  />
                  <label className="mt-2 block text-[11px] font-medium text-slate-600" htmlFor="crc-recover-email">
                    Email on checkout (optional)
                  </label>
                  <input
                    id="crc-recover-email"
                    type="email"
                    className={brandFormInput}
                    value={recoverEmail}
                    onChange={(e) => setRecoverEmail(e.target.value)}
                    placeholder="Same as /start if you want extra verification"
                    autoComplete="email"
                  />
                  {recoverMsg ? (
                    <p className="mt-2 text-xs text-slate-700">{recoverMsg}</p>
                  ) : null}
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-3 w-full rounded-xl"
                    disabled={recoverBusy}
                    onClick={() => void submitPaymentRecovery()}
                  >
                    {recoverBusy ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                        Linking payment…
                      </>
                    ) : (
                      'Restore my checkout & continue'
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlanId(plan.id)}
                      className={`w-full rounded-2xl border px-4 py-4 text-left shadow-sm transition ${
                        plan.id === selectedPlanId
                          ? 'border-sky-400/90 bg-gradient-to-br from-sky-50 via-white to-teal-50/60 ring-2 ring-sky-300/70 shadow-md shadow-sky-900/5'
                          : 'border-slate-200/90 bg-white/90 hover:border-sky-200 hover:bg-gradient-to-br hover:from-sky-50/40 hover:to-white'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-gray-900">{plan.name}</span>
                        <span className="rounded-full bg-gradient-to-r from-blue-600 to-teal-600 px-2.5 py-0.5 text-xs font-bold text-white shadow-sm">
                          ₹{(Number(plan.pricePaise || 0) / 100).toFixed(0)}
                        </span>
                      </div>
                      {plan.id === selectedPlanId &&
                      appliedVoucher &&
                      appliedVoucher.percentOff > 0 &&
                      appliedVoucher.percentOff < 100 ? (
                        <p className="mt-2 text-xs font-medium text-teal-900">
                          <span className="text-slate-400 line-through">
                            ₹{(Number(plan.pricePaise || 0) / 100).toFixed(0)} list
                          </span>
                          <span className="mx-1.5 text-slate-300">→</span>
                          <span className="font-semibold text-teal-800">
                            ₹
                            {(amountPaiseAfterVoucherPercent(Number(plan.pricePaise || 0), appliedVoucher.percentOff) / 100).toFixed(0)}{' '}
                            after code ({appliedVoucher.percentOff}% off)
                          </span>
                        </p>
                      ) : null}
                      {plan.badge ? (
                        <span className="mt-2 inline-block rounded-full border border-teal-200/80 bg-teal-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-teal-800">
                          {plan.badge}
                        </span>
                      ) : null}
                      {Array.isArray(plan.bullets) && plan.bullets.length ? (
                        <ul className="mt-2 list-inside list-disc text-xs leading-relaxed text-slate-600">
                          {plan.bullets.slice(0, 4).map((b) => (
                            <li key={b}>{b}</li>
                          ))}
                        </ul>
                      ) : null}
                    </button>
                  ))}
                </div>

                {starter && selectedPlanId === 'starter' ? (
                  <div className="mt-5 rounded-2xl border border-teal-200/60 bg-gradient-to-br from-teal-50/80 via-sky-50/40 to-white p-4 text-sm text-teal-950 shadow-inner shadow-teal-900/5">
                    <p className="font-display text-sm font-semibold text-teal-900">Why ₹1299 Starter</p>
                    <ul className="mt-2 list-inside list-disc text-xs leading-relaxed text-teal-900/90">
                      {(starter.bullets || []).map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="mt-8 space-y-3 rounded-2xl border border-sky-100/80 bg-gradient-to-br from-sky-50/50 to-teal-50/30 p-4 sm:p-5">
                  <p className={brandFormSectionKicker}>School, partner, or full waiver code</p>
                  <p className="text-xs leading-relaxed text-slate-600">
                    Apply a team code for a percent discount (Razorpay charges the reduced amount), or a 100% code to skip
                    payment.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                    <input
                      className={`${brandFormInput} flex-1 text-sm`}
                      placeholder="School or partner code"
                      value={voucher}
                      onChange={(e) => {
                        setVoucher(e.target.value);
                        setVoucherMsg('');
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={payBusy}
                      className="h-auto w-full shrink-0 rounded-xl border-2 border-sky-200/90 bg-white py-2.5 text-sm font-semibold text-sky-800 shadow-sm hover:border-teal-300 hover:bg-gradient-to-br hover:from-sky-50 hover:to-teal-50 sm:w-auto sm:px-6"
                      onClick={() => void applyVoucher()}
                    >
                      Apply
                    </Button>
                  </div>
                  <Button
                    type="button"
                    className={brandFormPrimaryCta}
                    disabled={payBusy || !rzpKey || checkoutPaise < 100}
                    onClick={() => void payRazorpay()}
                  >
                    {payBusy ? (
                      <>
                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                        Processing…
                      </>
                    ) : (
                      `Pay ₹${(checkoutPaise / 100).toFixed(0)} with Razorpay`
                    )}
                  </Button>
                  {!rzpKey ? (
                    <p className="text-center text-xs font-medium text-amber-900">Razorpay is not configured on this server.</p>
                  ) : null}
                  {voucherMsg ? (
                    <p className="rounded-lg border border-amber-200/70 bg-amber-50/90 px-3 py-2 text-center text-sm text-amber-950">
                      {voucherMsg}
                    </p>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
        <p className="text-center text-sm text-slate-600">
          <Link className="font-semibold text-blue-700 underline-offset-2 hover:text-teal-800 hover:underline" to="/start">
            Edit my details
          </Link>
          <span className="text-slate-300"> · </span>
          <Link className="font-semibold text-blue-700 underline-offset-2 hover:text-teal-800 hover:underline" to="/">
            Home
          </Link>
        </p>
      </div>
    </div>
  );
}
